import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { Paper, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";

export interface DataDependencyScope {
  icon: string;
  name: string;
}

interface DataDependenciesProps {
  scopes?: DataDependencyScope[];
}

const useStyles = makeStyles(theme => ({
  root: {
    display: "inline-block",
    width: "auto",
    maxWidth: "100%",
    minWidth: 320,
    boxSizing: "border-box",
    alignSelf: "flex-start",
    padding: theme.spacing(2),
    border: "1px solid #d1d5db",
    borderRadius: 4,
    boxShadow: "none",
    backgroundColor: "#F9FAFB"
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  icon: {
    width: 20,
    height: 20,
    color: "#374151",
    flexShrink: 0
  },
  headingBlock: {
    display: "flex",
    flexDirection: "column"
  },
  title: {
    fontWeight: 500,
    fontSize: 16,
    lineHeight: "20px",
    color: "#111827",
    marginBottom: 6
  },
  text: {
    fontSize: 12,
    lineHeight: "16px",
    color: "#4b5563",
    marginBottom: theme.spacing(1)
  },
  indentedContent: {
    marginLeft: 32
  },
  scopes: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  scopeItem: {
    display: "flex",
    alignItems: "center",
    flex: "0 1 auto",
    minWidth: 0,
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    backgroundColor: "#f9fafb",
    borderRadius: 4,
    border: "1px solid #d1d5db",
    maxWidth: "100%"
  },
  scopeIcon: {
    width: 20,
    height: 20,
    objectFit: "contain",
    flexShrink: 0,
    [theme.breakpoints.up("sm")]: {
      width: 24,
      height: 24
    }
  },
  scopeName: {
    color: "#111827",
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 500,
    overflowWrap: "anywhere"
  },
  footerText: {
    fontSize: 12,
    lineHeight: "16px",
    color: "#4b5563"
  }
}));

const DataDependencies: React.FC<DataDependenciesProps> = ({ scopes = [] }) => {
  const classes = useStyles();

  if (!scopes.length) {
    return null;
  }

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <AccountTreeOutlinedIcon className={classes.icon} />
        <div className={classes.headingBlock}>
          <Typography className={classes.title}>Data Dependencies</Typography>
          <Typography className={classes.text}>
            This service requires access to the following data scopes:
          </Typography>
        </div>
      </div>

      <div className={classes.indentedContent}>
        <div className={classes.scopes}>
          {scopes.map((scope, index) => (
            <div key={`${scope.name}-${index}`} className={classes.scopeItem}>
              <img
                src={scope.icon}
                alt={scope.name}
                className={classes.scopeIcon}
                onError={event => {
                  event.currentTarget.style.display = "none";
                }}
              />
              <Typography className={classes.scopeName}>
                {scope.name}
              </Typography>
            </div>
          ))}
        </div>

        <Typography className={classes.footerText}>
          Callers must provide a GrantToken for these scopes.
        </Typography>
      </div>
    </Paper>
  );
};

export default DataDependencies;
